import type { DatasetColumn, DatasetInput, TranslationRule, UntranslatedCount } from '../../../types/backend';

type ColumnType = DatasetColumn['type'];

/** A column's source in one input: a path, null when the input does not provide it, or rules. */
export type ColumnSource = DatasetInput['mapping'][string];

// The backend's MAX_INPUTS and MAX_RULES (aggregates/event_dataset_mapping.py).
export const MAX_INPUTS = 4;
export const MAX_RULES = 4;
// The backend's MAX_VALUES, MAX_BANDS and MAX_VALUE_KEY: the most a rule takes.
export const MAX_VALUES = 200;
export const MAX_BANDS = 20;
const MAX_VALUE_KEY = 256;
export const EVENT_ROOT = '$event.';
// The backend's path grammar (aggregates/event_dataset_mapping.py `DOTTED_PATH`): keys joined by '.', each with optional list indexes.
const PATH_SEGMENT = String.raw`[A-Za-z0-9_\-]+(?:\[[0-9]+\])*`;
const DATASET_PATH = new RegExp(String.raw`^${PATH_SEGMENT}(?:\.${PATH_SEGMENT})*$`);
export const PATH_REFUSED = 'Keys may hold only letters, digits, _ and -';
export const PATH_MISSING = 'Pick a path from the sample, starting with $';
const ZONED_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
// The backend translates no list or JSON value; such a column takes paths only.
const UNTRANSLATED: ColumnType[] = ['list', 'json'];
const NOT_PROVIDED_REFUSED = 'Every row needs this column, so this input must give it';
const TRANSLATION_REFUSED = 'A list or JSON column takes a path, not a translation';
const IDENTITY_TRANSLATION_REFUSED = 'A key or the ordering field takes a path, not a translation';
const ENVELOPE_TRANSLATION_REFUSED =
  'Only a value list into a text column translates an envelope value, and only id, source, detail-type, account, region or resources[n]';
// The envelope values the backend translates (aggregates/event_dataset_mapping.py): text, read with one index at most.
const TRANSLATABLE_ENVELOPE = /^(?:id|source|detail-type|account|region|resources\[[0-9]+\])$/;

export type Translation = 'none' | 'values' | 'bands';

/** A source value and what it becomes, as typed: the output is converted to the column type on save. */
export interface ValueRow {
  from: string;
  to: string;
  /** Saved on an existing dataset: changing or removing it is a breaking change. */
  locked: boolean;
}

export interface BandRow {
  min: string;
  max: string;
  to: string;
  locked: boolean;
}

export interface RuleForm {
  /** A `$.`-form path, or a `$event.` path. */
  path: string;
  translate: Translation;
  values: ValueRow[];
  bands: BandRow[];
  /** Values that pass to the next rule. */
  skip: string[];
  /** Saved on an existing dataset: its path, kind and skip values are fixed; entries may be added. */
  locked: boolean;
}

/** Where a column reads from in one input. */
export type SourceForm =
  | { kind: 'path'; path: string }
  | { kind: 'none' }
  | { kind: 'rules'; rules: RuleForm[] };

/** One event format feeding the dataset. */
export interface InputForm {
  eventPattern: Record<string, unknown>;
  resourcePath: string;
  /** Saved on an existing dataset: inputs are identified by position, so it is fixed. */
  locked: boolean;
}

export const emptyRule = (): RuleForm => ({ path: '', translate: 'none', values: [], bands: [], skip: [], locked: false });
export const emptyInput = (eventPattern: Record<string, unknown> = {}): InputForm => ({ eventPattern, resourcePath: '', locked: false });
export const emptySource = (): SourceForm => ({ kind: 'path', path: '' });
export const inputLabel = (input: number) => `Input ${input + 1}`;

/** The backend reads `a.b[0]` from the row (or event) and `$event.a` from the event root. */
export function toDatasetPath(formPath: string): string {
  const trimmed = formPath.trim();
  if (trimmed.startsWith(EVENT_ROOT)) return trimmed;
  return trimmed.replace(/^\$\.?/, '');
}

export function fromDatasetPath(datasetPath: string): string {
  return datasetPath.startsWith(EVENT_ROOT) ? datasetPath : `$.${datasetPath}`;
}

/** True when the backend can read the path: `$.`-form or, where allowed, `$event.`-form. */
export function isDatasetPath(formPath: string, allowEventRoot: boolean): boolean {
  const trimmed = formPath.trim();
  if (trimmed.startsWith(EVENT_ROOT)) return allowEventRoot && DATASET_PATH.test(trimmed.slice(EVENT_ROOT.length));
  return DATASET_PATH.test(trimmed.replace(/^\$\.?/, ''));
}

/** Why the backend could not read a mapping path, or undefined when it can. */
export function pathProblem(formPath: string): string | undefined {
  if (!formPath.trim().startsWith('$') || !toDatasetPath(formPath)) return PATH_MISSING;
  return isDatasetPath(formPath, true) ? undefined : PATH_REFUSED;
}

export const canTranslate = (type: ColumnType) => !UNTRANSLATED.includes(type);

function outputText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

// Output typed to the column. A text that is not of the type is sent as typed, so the backend names it; the
// form refuses it before a save or a preview.
function outputValue(text: string, type: ColumnType): unknown {
  if (type === 'number' && text.trim() && Number.isFinite(Number(text))) return Number(text);
  if (type === 'boolean' && (text === 'true' || text === 'false')) return text === 'true';
  return text;
}

function ruleForm(rule: TranslationRule, locked: boolean): RuleForm {
  return {
    path: fromDatasetPath(rule.path),
    translate: rule.values ? 'values' : rule.bands ? 'bands' : 'none',
    values: Object.entries(rule.values ?? {}).map(([from, to]) => ({ from, to: outputText(to), locked })),
    bands: (rule.bands ?? []).map((b) => ({ min: String(b.min), max: String(b.max), to: outputText(b.value), locked })),
    skip: rule.continue_on ?? [],
    locked,
  };
}

export function sourceForm(source: ColumnSource, locked: boolean): SourceForm {
  if (source === null) return { kind: 'none' };
  if (typeof source === 'string') return { kind: 'path', path: fromDatasetPath(source) };
  return { kind: 'rules', rules: source.first_of.map((r) => ruleForm(r, locked)) };
}

function toRule(rule: RuleForm, type: ColumnType): TranslationRule {
  return {
    path: toDatasetPath(rule.path),
    ...(rule.translate === 'values'
      ? { values: Object.fromEntries(rule.values.map((v) => [v.from, outputValue(v.to, type)])) }
      : {}),
    ...(rule.translate === 'bands'
      ? { bands: rule.bands.map((b) => ({ min: Number(b.min), max: Number(b.max), value: outputValue(b.to, type) })) }
      : {}),
    ...(rule.skip.length > 0 ? { continue_on: rule.skip } : {}),
  };
}

export function toColumnSource(source: SourceForm, type: ColumnType): ColumnSource {
  if (source.kind === 'path') return toDatasetPath(source.path);
  if (source.kind === 'none') return null;
  return { first_of: source.rules.map((r) => toRule(r, type)) };
}

/** The path a source reads first: its path, or its first rule's. */
export function primaryPath(source: SourceForm): string {
  if (source.kind === 'path') return source.path;
  return source.kind === 'rules' ? source.rules[0]?.path ?? '' : '';
}

export function ruleCountLabel(source: SourceForm): string {
  if (source.kind !== 'rules') return 'Translate…';
  return `Translate (${source.rules.length} ${source.rules.length === 1 ? 'rule' : 'rules'})`;
}

function outputProblem(text: string, type: ColumnType): string | undefined {
  if (type === 'number' && (!text.trim() || !Number.isFinite(Number(text)))) return 'Give a number';
  if (type === 'boolean' && text !== 'true' && text !== 'false') return 'Pick true or false';
  if (type === 'timestamp' && !ZONED_TIME.test(text)) return 'Give a date and time with a zone, such as 2026-11-04T10:00:00Z';
  return undefined;
}

const isNumber = (text: string) => text.trim() !== '' && Number.isFinite(Number(text));

function bandProblems(bands: BandRow[], type: ColumnType, at: string, errors: Record<string, string>) {
  bands.forEach((b, i) => {
    if (!isNumber(b.min)) errors[`${at}.band.${i}.min`] = 'Give a number';
    if (!isNumber(b.max)) errors[`${at}.band.${i}.max`] = 'Give a number';
    else if (isNumber(b.min) && Number(b.max) < Number(b.min)) errors[`${at}.band.${i}.max`] = 'Max must be at least min';
    const output = outputProblem(b.to, type);
    if (output) errors[`${at}.band.${i}.to`] = output;
  });
  const ordered = bands
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => isNumber(b.min) && isNumber(b.max))
    .sort((x, y) => Number(x.b.min) - Number(y.b.min));
  for (let n = 1; n < ordered.length; n += 1) {
    const lower = ordered[n - 1].b;
    const upper = ordered[n];
    if (Number(upper.b.min) <= Number(lower.max)) {
      errors[`${at}.band.${upper.i}.min`] ??= `Overlaps the band ${lower.min} to ${lower.max}`;
    }
  }
}

/** Where a column's rules read and what they fill, beyond its type. */
export interface RulePlace {
  /** A key field or the ordering field: it reads values as they are, so it takes fallback paths but no translation. */
  identifies: boolean;
  /** The dataset has a row path, so a path without `$event.` reads the row, not the event envelope. */
  hasRowPath: boolean;
}

const ANYWHERE: RulePlace = { identifies: false, hasRowPath: false };

/** Whether a path reads the raw event envelope (source, account, id, resources and so on) rather than detail. */
export function readsEnvelope(formPath: string, hasRowPath: boolean): boolean {
  const trimmed = formPath.trim();
  if (!trimmed.startsWith(EVENT_ROOT) && hasRowPath) return false;
  const first = envelopePath(trimmed).split('.')[0].split('[')[0];
  return first !== '' && first !== 'detail';
}

const envelopePath = (formPath: string) => toDatasetPath(formPath).replace(/^\$event\./, '');

/** Every refusal of a column's rules, keyed by the field it belongs to: `rule.0.path`, `rule.1.band.0.min`. */
export function ruleProblems(rules: RuleForm[], type: ColumnType, place: RulePlace = ANYWHERE): Record<string, string> {
  const errors: Record<string, string> = {};
  if (rules.length === 0) errors.rules = 'Add a rule';
  if (rules.length > MAX_RULES) errors.rules = `A column takes at most ${MAX_RULES} rules`;
  rules.forEach((rule, r) => {
    const at = `rule.${r}`;
    const path = pathProblem(rule.path);
    if (path) errors[`${at}.path`] = path;
    if (rule.translate !== 'none' && !canTranslate(type)) errors[`${at}.translate`] = TRANSLATION_REFUSED;
    else if (rule.translate !== 'none' && place.identifies) errors[`${at}.translate`] = IDENTITY_TRANSLATION_REFUSED;
    else if (rule.translate !== 'none' && readsEnvelope(rule.path, place.hasRowPath)
      && (rule.translate === 'bands' || type !== 'text' || !TRANSLATABLE_ENVELOPE.test(envelopePath(rule.path)))) {
      errors[`${at}.translate`] = ENVELOPE_TRANSLATION_REFUSED;
    }
    if (rule.translate === 'values') {
      if (rule.values.length === 0) errors[`${at}.values`] = 'Add a value, or read the value as it is';
      if (rule.values.length > MAX_VALUES) errors[`${at}.values`] = `A rule takes at most ${MAX_VALUES} values`;
      const seen = new Set<string>();
      rule.values.forEach((v, i) => {
        if (v.from.length > MAX_VALUE_KEY) errors[`${at}.value.${i}.from`] = `At most ${MAX_VALUE_KEY} characters`;
        else if (seen.has(v.from)) errors[`${at}.value.${i}.from`] = `${v.from} is listed twice`;
        seen.add(v.from);
        const output = outputProblem(v.to, type);
        if (output) errors[`${at}.value.${i}.to`] = output;
      });
    }
    if (rule.translate === 'bands') {
      if (rule.bands.length === 0) errors[`${at}.bands`] = 'Add a band, or read the value as it is';
      if (rule.bands.length > MAX_BANDS) errors[`${at}.bands`] = `A rule takes at most ${MAX_BANDS} bands`;
      bandProblems(rule.bands, type, at, errors);
    }
    const translated = rule.translate === 'values' ? rule.values.map((v) => v.from) : [];
    const both = rule.skip.find((s) => translated.includes(s));
    if (rule.skip.length > 0 && r === rules.length - 1) errors[`${at}.skip`] = 'The last rule has no next rule to skip to';
    else if (both !== undefined) errors[`${at}.skip`] = `${both} is both translated and skipped`;
  });
  return errors;
}

/** Where a column stands in the dataset, which decides what its sources may be. */
export interface ColumnRole extends RulePlace {
  type: ColumnType;
  /** Every row must have it: a required column, a key field or the ordering field. */
  alwaysFilled: boolean;
}

/** Whether a column's rules may translate values: not a list or JSON column, a key or the ordering field. */
export const takesTranslation = (role: ColumnRole) => canTranslate(role.type) && !role.identifies;

/** Why a column's source in one input is refused, or undefined. */
export function sourceProblem(source: SourceForm, role: ColumnRole): string | undefined {
  const { type } = role;
  if (source.kind === 'path') return pathProblem(source.path);
  if (source.kind === 'none') return role.alwaysFilled ? NOT_PROVIDED_REFUSED : undefined;
  const translates = source.rules.some((r) => r.translate !== 'none');
  if (translates && !canTranslate(type)) return TRANSLATION_REFUSED;
  if (translates && role.identifies) return IDENTITY_TRANSLATION_REFUSED;
  return Object.keys(ruleProblems(source.rules, type, role)).length > 0 ? 'Fix the translation rules' : undefined;
}

// Patterns compare as the backend's dicts do: key order does not count.
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/** The refusal of each input's pattern, by position: empty, or the same as an earlier input's. */
export function patternProblems(inputs: InputForm[]): Record<number, string> {
  const problems: Record<number, string> = {};
  const seen = inputs.map((i) => canonical(i.eventPattern));
  inputs.forEach((input, n) => {
    const earlier = seen.indexOf(seen[n]);
    if (Object.keys(input.eventPattern).length === 0) problems[n] = 'Event pattern is required';
    else if (earlier < n) problems[n] = `Same pattern as ${inputLabel(earlier)}; each input is its own format`;
  });
  return problems;
}

const INPUT_KEY = /^input\.(\d+)\.(.+)$/;

/**
 * The errors one input's fields show: its own under the names the sections use (`eventPattern`, `resourcePath`,
 * `column.0.path`), and the dataset-wide ones as they are.
 */
export function inputErrors(errors: Record<string, string>, input: number): Record<string, string> {
  const shown: Record<string, string> = {};
  for (const [key, message] of Object.entries(errors)) {
    const match = INPUT_KEY.exec(key);
    if (!match) shown[key] = message;
    else if (Number(match[1]) === input) shown[match[2].replace(/^column\.(\d+)\.source$/, 'column.$1.path')] = message;
  }
  return shown;
}

export function inputsWithErrors(errors: Record<string, string>): Set<number> {
  return new Set(Object.keys(errors).map((k) => INPUT_KEY.exec(k)).filter((m) => m !== null).map((m) => Number(m[1])));
}

/** Every error as one list, each of an input named after it when there are several. */
export function problemList(errors: Record<string, string>, inputCount: number): string[] {
  return Object.entries(errors).map(([key, message]) => {
    const match = INPUT_KEY.exec(key);
    return match && inputCount > 1 ? `${inputLabel(Number(match[1]))}: ${message}` : message;
  });
}


export interface UntranslatedGroup {
  column: string;
  /** Every untranslated value of the column, over its inputs. */
  total: number;
  /** One line per listed value, then one for the values the count holds but does not list. */
  lines: string[];
}

/** Untranslated counts by column; each value named after its input when the dataset has several. */
export function untranslatedGroups(counts: UntranslatedCount[], inputCount: number): UntranslatedGroup[] {
  const groups = new Map<string, UntranslatedGroup>();
  for (const c of counts) {
    const group = groups.get(c.column) ?? { column: c.column, total: 0, lines: [] };
    const named = inputCount > 1 ? `${inputLabel(c.input)}, ` : '';
    group.total += c.count;
    for (const v of c.values) group.lines.push(`${named}${v.value}: ${v.count}`);
    // The backend lists the most frequent values only; the rest are counted, not dropped.
    const unlisted = c.count - c.values.reduce((sum, v) => sum + v.count, 0);
    if (unlisted > 0) group.lines.push(`${named}other values: ${unlisted}`);
    groups.set(c.column, group);
  }
  return [...groups.values()];
}
